import { computed, ref, watch } from 'vue';
import type { Ref } from 'vue';
import { EmailAiDraftState } from '@/components/lead_xpress/aiDraft/useEmailAiDraft.ts';

interface UseEmailAiDraftFeedbackVisibilityOptions {
    body: Readonly<Ref<string | null | undefined>>;
    hasGeneratedDraft: Readonly<Ref<boolean>>;
    state: Readonly<Ref<EmailAiDraftState>>;
}

const normalizeBody = (body: string | null | undefined) => body || '';

const normalizeWhitespace = (value: string) => value.replaceAll(/\s+/g, ' ').trim();

const normalizeBodyText = (body: string) => {
    if (!body) {
        return '';
    }

    if (typeof DOMParser !== 'undefined') {
        const textContent =
            new DOMParser().parseFromString(body, 'text/html').body.textContent || '';

        return normalizeWhitespace(textContent);
    }

    return normalizeWhitespace(body);
};

const bodiesHaveEquivalentText = (firstBody: string, secondBody: string) =>
    normalizeBodyText(firstBody) === normalizeBodyText(secondBody);

export const useEmailAiDraftFeedbackVisibility = ({
    body,
    hasGeneratedDraft,
    state,
}: UseEmailAiDraftFeedbackVisibilityOptions) => {
    const feedbackBody = ref<string | null>(
        hasGeneratedDraft.value ? normalizeBody(body.value) : null
    );
    const pendingAppliedBody = ref<string | null>(null);
    const pendingFeedbackBody = ref<string | null>(null);
    const lastAppliedBody = ref<string | null>(null);

    const clearPendingBody = () => {
        pendingAppliedBody.value = null;
        pendingFeedbackBody.value = null;
    };

    const showFeedbackControls = computed<boolean>(
        () =>
            hasGeneratedDraft.value &&
            (feedbackBody.value !== null || pendingFeedbackBody.value !== null)
    );

    const waitForAppliedBody = () => {
        const normalizedBody = normalizeBody(body.value);

        feedbackBody.value = null;

        if (lastAppliedBody.value === normalizedBody) {
            feedbackBody.value = normalizedBody;
            clearPendingBody();

            return;
        }

        pendingAppliedBody.value = normalizedBody;
        pendingFeedbackBody.value = normalizedBody;
    };

    const clear = () => {
        feedbackBody.value = null;
        clearPendingBody();
        lastAppliedBody.value = null;
    };

    const stateSupportsFeedback = (draftState = state.value) =>
        draftState === EmailAiDraftState.Inserted ||
        draftState === EmailAiDraftState.UpdateUnsupported;

    const restoreFeedbackForAppliedBody = (normalizedBody: string) => {
        if (
            hasGeneratedDraft.value &&
            stateSupportsFeedback() &&
            feedbackBody.value === null &&
            pendingFeedbackBody.value === null &&
            lastAppliedBody.value !== null &&
            bodiesHaveEquivalentText(lastAppliedBody.value, normalizedBody)
        ) {
            feedbackBody.value = normalizedBody;
        }
    };

    const handleBodyChanged = (nextBody: string | null | undefined) => {
        const normalizedBody = normalizeBody(nextBody);

        if (pendingFeedbackBody.value !== null && pendingFeedbackBody.value !== normalizedBody) {
            if (bodiesHaveEquivalentText(pendingFeedbackBody.value, normalizedBody)) {
                if (stateSupportsFeedback()) {
                    feedbackBody.value = normalizedBody;
                    clearPendingBody();
                } else {
                    pendingFeedbackBody.value = normalizedBody;
                }
            } else if (stateSupportsFeedback()) {
                feedbackBody.value = normalizedBody;
                clearPendingBody();
            } else {
                clearPendingBody();
            }
        }

        if (feedbackBody.value !== null && feedbackBody.value !== normalizedBody) {
            feedbackBody.value = bodiesHaveEquivalentText(feedbackBody.value, normalizedBody)
                ? normalizedBody
                : null;
        }

        restoreFeedbackForAppliedBody(normalizedBody);
    };

    const handleComposerModelValueApplied = (appliedBody: string) => {
        lastAppliedBody.value = appliedBody;

        if (pendingAppliedBody.value === appliedBody) {
            feedbackBody.value = pendingFeedbackBody.value;
            clearPendingBody();
        }

        restoreFeedbackForAppliedBody(normalizeBody(body.value));
    };

    watch(hasGeneratedDraft, (nextHasGeneratedDraft) => {
        if (nextHasGeneratedDraft) {
            waitForAppliedBody();

            return;
        }

        clear();
    });

    watch(state, (nextState, previousState) => {
        if (
            previousState === EmailAiDraftState.Loading &&
            stateSupportsFeedback(nextState) &&
            hasGeneratedDraft.value
        ) {
            waitForAppliedBody();
        }
    });

    watch(body, (nextBody) => {
        handleBodyChanged(nextBody);
    });

    return {
        handleBodyChanged,
        handleComposerModelValueApplied,
        showFeedbackControls,
    };
};
